import * as React from 'react';
import type { ComponentPageProps } from '@web-config-app/core';

/**
 * React component type for module component pages.
 * Each registered component receives standardized ComponentPageProps.
 */
export type ModulePageComponent = React.ComponentType<ComponentPageProps>;

/**
 * Registry of component page modules. The key is the module ID from the
 * module config YAML, and the value is a lazy-loaded React component.
 *
 * Each component is loaded on-demand via React.lazy() so that page bundles
 * are only fetched when the user navigates to them. The module router wraps
 * these in a Suspense boundary.
 *
 * All component pages live under pages/{module-id}/ and are re-exported
 * through a local file so imports are consistent.
 *
 * IMPORTANT: every `componentPage` module declared in `.config-registry`
 * (see `.config-registry/modules/component-pages/*.yaml`) must have a
 * matching entry in this registry — the module ID used here is the `id`
 * from the YAML file. If the IDs don't line up, `ComponentPageElement`
 * will render an `<ErrorPage />` at runtime with no other signal. There
 * is no build-time check enforcing this coupling today; a dev-time
 * warning fires in `ComponentPageElement` when a missing ID is rendered.
 */
export const moduleComponents: Record<
  string,
  React.LazyExoticComponent<ModulePageComponent>
> = {
  'feature-flags': React.lazy(() =>
    import('../pages/component-pages/feature-flags/feature-flags.page').then(
      (mod) => ({
        default: mod.AdminFeatureFlagsPage,
      }),
    ),
  ),
  'activity-translation': React.lazy(() =>
    import(
      '../pages/component-pages/activity-translation/activity-translation.page'
    ).then((mod) => ({
      default: mod.ConfigAppActivityTranslationPage,
    })),
  ),
  'example-component-page': React.lazy(() =>
    import(
      '../pages/component-pages/example-component-page/example-component-page'
    ).then((mod) => ({ default: mod.ExampleComponentPage })),
  ),
  'journey-landing': React.lazy(() =>
    import(
      '../pages/component-pages/journey-landing/journey-landing.page'
    ).then((mod) => ({ default: mod.JourneyLandingPage })),
  ),
  'kpi-dashboard': React.lazy(() =>
    import(
      '../pages/component-pages/analytics-dashboard/analytics-dashboard.page'
    ).then((mod) => ({ default: mod.AnalyticsDashboardPage })),
  ),
  'self-reported-dashboard': React.lazy(() =>
    import(
      '../pages/component-pages/analytics-dashboard/analytics-dashboard.page'
    ).then((mod) => ({ default: mod.AnalyticsDashboardPage })),
  ),
  'template-interpreter': React.lazy(() =>
    import('@web-config-app/template-interpreter').then((mod) => ({
      default: mod.TemplateInterpreterPage,
    })),
  ),
};
